/**
 * The one branded wait in both web apps: a jumping X9 mark on paper.
 *
 * A cold boot still crosses several gates (portal → work chunk → board). Each
 * gate used to mount its own loader with a different layout, so the mark
 * appeared to hop between three seats and the bounce restarted each time.
 * Every instance now uses the same viewport-centered stage, and the bounce
 * is phase-locked to a tab-lifetime clock so a remount continues the jump
 * that is already in flight.
 */
import { useTranslation } from 'react-i18next';
import X9Logo from './X9Logo';

/** Must stay in lock-step with `theme.extend.animation['logo-jump']`. */
export const LOGO_JUMP_MS = 850;

const jumpStartedAt = typeof performance !== 'undefined' ? performance.now() : 0;

/** Negative delay so a remounted loader is mid-jump, not at rest. */
export function logoJumpDelayMs(now = typeof performance !== 'undefined' ? performance.now() : 0): number {
  const elapsed = now - jumpStartedAt;
  if (!Number.isFinite(elapsed) || elapsed <= 0) return 0;
  return -(elapsed % LOGO_JUMP_MS);
}

interface LoadingScreenProps {
  label?: string;
  /**
   * Reserve the same space the ready-state CTA occupies. This prevents
   * the logo/body stack from jumping when validation resolves and the
   * welcome CTA fades in.
   */
  reserveCtaSlot?: boolean;
  /**
   * Kept so callers can say this wait is covering an already-painted
   * screen. Visually identical to the page loader — the stage is always
   * viewport-centered — so portal → chunk → board never changes seat.
   */
  overlay?: boolean;
}

const STAGE =
  'fixed inset-0 z-[200] flex flex-col items-center justify-center bg-background px-6 text-center';

export default function LoadingScreen({
  label,
  reserveCtaSlot = false,
}: LoadingScreenProps) {
  const { t } = useTranslation('web');
  const text = label ?? t('shell.opening');

  return (
    <div
      className={STAGE}
      role="status"
      aria-live="polite"
      aria-busy
    >
      <div
        className="animate-logo-jump w-[min(25vw,5.7rem)] shrink-0 text-x9-marine will-change-transform"
        style={{ animationDelay: `${logoJumpDelayMs()}ms` }}
      >
        <X9Logo className="block w-full select-none" />
      </div>
      <p className="mt-10 max-w-xs text-sm font-medium tracking-wide text-x9-marineSoft">
        {text}
      </p>
      {reserveCtaSlot && (
        <div className="mt-10 h-[3.5rem] w-full max-w-md opacity-0" aria-hidden="true">
          <div className="btn-paper-primary w-full">Placeholder</div>
        </div>
      )}
    </div>
  );
}
